<script lang="ts">
	import Chart from "chart.js/auto";
	import type { Chart as ChartInstance, ChartOptions, TooltipModel } from "chart.js";
	import { formatCurrency, decimalToPercent } from "#lib/shared/formatters.js";
	import { onDestroy } from "svelte";

	let {
		data,
		colors
	}: {
		data: {
			label: string;
			initialInvestment: number;
			finalValue: number;
			totalReturn: number;
			annualizedReturn: number;
		}[];
		colors: string[];
	} = $props();

	let chartCanvas: HTMLCanvasElement;
	let chartInstance: ChartInstance | undefined = undefined;
	let tooltipEl: HTMLDivElement;

	// Reactive variables for tooltip content
	let tooltipData = $state({
		title: "",
		finalValue: "",
		totalReturn: "",
		annualizedReturn: "",
		colorIndicator: "",
		opacity: "0"
	});

	const chartDataset = $derived({
		label: "Total Return",
		data: data.map((item) => item.finalValue),
		backgroundColor: colors,
		borderColor: colors,
		borderWidth: 1
	});

	// Function to handle custom tooltip
	const externalTooltipHandler = (context: {
		chart: ChartInstance;
		tooltip: TooltipModel<"bar">;
	}) => {
		const { chart, tooltip } = context;

		// Hide tooltip when hovering away
		if (tooltip.opacity === 0) {
			tooltipData.opacity = "0";
			return;
		}

		if (tooltip.body) {
			const dataIndex = tooltip.dataPoints?.[0]?.dataIndex;
			if (dataIndex !== undefined) {
				const item = data[dataIndex];

				tooltipData.title = item?.label ?? "";
				tooltipData.finalValue = formatCurrency(item?.finalValue ?? 0);
				tooltipData.totalReturn = formatCurrency(item?.totalReturn ?? 0);
				tooltipData.annualizedReturn = decimalToPercent(item?.annualizedReturn ?? 0);
				tooltipData.colorIndicator = colors[dataIndex] ?? "";
			}
		}

		// Position the tooltip
		const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

		tooltipData.opacity = "1";
		tooltipEl.style.left = positionX + tooltip.caretX + "px";
		tooltipEl.style.top = positionY + tooltip.caretY + "px";
	};

	const chartOptions: ChartOptions<"bar"> = {
		scales: {
			y: {
				title: {
					display: true,
					text: "Total Return"
				},
				ticks: {
					callback: (value) => formatCurrency(value as number)
				}
			}
		},
		plugins: {
			tooltip: {
				enabled: false,
				position: "nearest",
				external: externalTooltipHandler
			},
			legend: {
				display: false
			}
		}
	};

	$effect(() => {
		if (chartCanvas && !chartInstance) {
			chartInstance = new Chart(chartCanvas, {
				type: "bar",
				data: {
					labels: data.map((item) => item.label),
					datasets: [chartDataset]
				},
				options: chartOptions
			});
		} else if (chartInstance) {
			chartInstance.data = {
				labels: data.map((item) => item.label),
				datasets: [chartDataset]
			};
			chartInstance.update();
		}
	});

	onDestroy(() => {
		chartInstance?.destroy();
	});
</script>

<div class="relative">
	<canvas bind:this={chartCanvas}></canvas>

	<!-- Custom tooltip -->
	<div
		bind:this={tooltipEl}
		class="bg-card pointer-events-none absolute z-10 -translate-x-1/2 transform rounded border p-3 shadow-lg transition-opacity duration-100"
		style="min-width: 200px; opacity: {tooltipData.opacity};"
	>
		<div class="mb-2 flex items-center gap-2">
			<div
				class="color-indicator h-3 w-3 rounded-full"
				style="background-color: {tooltipData.colorIndicator};"
			></div>
			<p class="tooltip-title text-lg font-bold">{tooltipData.title}</p>
		</div>
		<div class="space-y-1 text-sm">
			<p>
				Final Value: <span class="final-value font-semibold">{tooltipData.finalValue}</span>
			</p>
			<p>
				Total Return: <span class="total-return font-semibold"
					>{tooltipData.totalReturn}</span
				>
			</p>
			<p>
				Annualized Return: <span class="annualized-return font-semibold"
					>{tooltipData.annualizedReturn}</span
				>
			</p>
		</div>
	</div>
</div>
